/**
 * TTS — Hybrid text-to-speech with neural and browser fallback.
 *
 * Three-tier audio strategy:
 * 1. Kokoro neural TTS (English only, ~92 MB, 8/10 quality) — best quality
 * 2. Web Speech API (all languages) — always available fallback
 * 3. Pre-recorded MP3 greetings — guaranteed quality for startup
 *
 * Language routing:
 *   English + Kokoro loaded → Kokoro (neural)
 *   English + Kokoro not ready → Web Speech API
 *   Non-English → Web Speech API (Kokoro JS lacks multilingual phonemizer)
 *
 * Mobile strategy:
 * - Try speaking directly first (works on most Android, desktop)
 * - If speech gets blocked (iOS Safari), queue it and play on first user tap
 * - On iOS, a silent utterance "unlocks" the audio context on user gesture
 */

import { getLangMeta } from '../i18n';
import { isKokoroReady, kokoroSpeak, kokoroStop, isKokoroSpeaking } from './kokoro-tts';

// Global mute state — checked before any speech
export function isMuted(): boolean {
  try { return localStorage.getItem('aftercare:muted') === 'true'; } catch { return false; }
}

export interface TTSOptions {
  rate?: number;
  pitch?: number;
  volume?: number;
}

// Defaults tuned for soothing, gentle nurse-like delivery
const DEFAULT_RATE = 0.82;
const DEFAULT_PITCH = 1.08;
const DEFAULT_VOLUME = 0.95;

// Preferred voices — sorted by most natural/warm sounding
const PREFERRED_VOICES = [
  'Samantha (Enhanced)',
  'Samantha',
  'Nicky (Enhanced)',
  'Nicky',
  'Karen (Enhanced)',
  'Karen',
  'Moira (Enhanced)',
  'Moira',
  'Tessa (Enhanced)',
  'Tessa',
  'Martha',
  'Google UK English Female',
  'Microsoft Zira',
  'Google US English',
];

function selectBestVoice(langTag?: string): SpeechSynthesisVoice | null {
  const voices = window.speechSynthesis.getVoices();
  if (voices.length === 0) return null;

  const targetLang = langTag || 'en-US';
  const langPrefix = targetLang.split('-')[0]; // 'es' from 'es-MX'

  // For the current language, try to find a matching voice
  if (langPrefix !== 'en') {
    // Exact match first (e.g., es-MX)
    const exact = voices.find(v => v.lang === targetLang);
    if (exact) {
      console.log(`[TTS] Voice for ${targetLang}:`, exact.name);
      return exact;
    }
    // Prefix match (e.g., any es-* voice), prefer female
    const prefixMatches = voices.filter(v => v.lang.startsWith(langPrefix));
    const female = prefixMatches.find(v => !v.name.toLowerCase().includes('male'));
    if (female) {
      console.log(`[TTS] Voice for ${langPrefix}:`, female.name);
      return female;
    }
    if (prefixMatches.length > 0) {
      console.log(`[TTS] Voice for ${langPrefix}:`, prefixMatches[0].name);
      return prefixMatches[0];
    }
  }

  // English: use preferred voice list
  for (const preferred of PREFERRED_VOICES) {
    const match = voices.find((v) => v.name === preferred || v.name.includes(preferred));
    if (match) {
      console.log('[TTS] Selected voice:', match.name);
      return match;
    }
  }

  const englishVoices = voices.filter(
    (v) => v.lang.startsWith('en') && !v.name.toLowerCase().includes('male'),
  );
  if (englishVoices.length > 0) return englishVoices[0];

  const anyEnglish = voices.find((v) => v.lang.startsWith('en'));
  if (anyEnglish) return anyEnglish;
  return voices[0];
}

/** Get voice for the current app language */
function getVoiceForCurrentLang(): SpeechSynthesisVoice | null {
  const meta = getLangMeta();
  return selectBestVoice(meta.speechLang);
}

let selectedVoice: SpeechSynthesisVoice | null = null;

/** Re-select the best voice for the current app language */
function refreshSelectedVoice(): void {
  selectedVoice = getVoiceForCurrentLang();
}

if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
  window.speechSynthesis.onvoiceschanged = refreshSelectedVoice;
  selectedVoice = selectBestVoice();
}

// When app language changes, update cached voice immediately
if (typeof document !== 'undefined') {
  document.addEventListener('aftercare:lang-change', refreshSelectedVoice);
}

// -----------------------------------------------------------------------
// TTS Engine
// -----------------------------------------------------------------------

interface QueueEntry {
  text: string;
  options: Required<TTSOptions>;
  resolve: () => void;
  reject: (err: Error) => void;
}

class TTSEngine {
  private rate: number = DEFAULT_RATE;
  private pitch: number = DEFAULT_PITCH;
  private volume: number = DEFAULT_VOLUME;
  private queue: QueueEntry[] = [];
  private speaking = false;

  speak(text: string, options?: TTSOptions): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const merged: Required<TTSOptions> = {
        rate: options?.rate ?? this.rate,
        pitch: options?.pitch ?? this.pitch,
        volume: options?.volume ?? this.volume,
      };
      this.queue.push({ text, options: merged, resolve, reject });
      this.processQueue();
    });
  }

  stop(): void {
    window.speechSynthesis.cancel();
    for (const entry of this.queue) {
      entry.reject(new Error('Speech cancelled'));
    }
    this.queue = [];
    this.speaking = false;
  }

  isSpeaking(): boolean {
    return this.speaking;
  }

  setRate(rate: number): void { this.rate = Math.max(0.1, Math.min(10, rate)); }
  setVolume(volume: number): void { this.volume = Math.max(0, Math.min(1, volume)); }
  setPitch(pitch: number): void { this.pitch = Math.max(0, Math.min(2, pitch)); }

  private processQueue(): void {
    if (this.speaking || this.queue.length === 0) return;

    const entry = this.queue.shift()!;
    this.speaking = true;

    // Force-resume in case speechSynthesis is stuck in paused state
    if (window.speechSynthesis.paused) {
      window.speechSynthesis.resume();
    }

    const utterance = new SpeechSynthesisUtterance(entry.text);
    utterance.rate = entry.options.rate;
    utterance.pitch = entry.options.pitch;
    utterance.volume = entry.options.volume;

    // Set language and voice based on current app language
    const langMeta = getLangMeta();
    utterance.lang = langMeta.speechLang;
    const langVoice = getVoiceForCurrentLang();
    if (langVoice) {
      utterance.voice = langVoice;
    } else if (selectedVoice) {
      utterance.voice = selectedVoice;
    }

    // Watchdog: mobile browsers can silently fail to speak. If neither onend
    // nor onerror fires within a generous timeout, force-advance the queue.
    const watchdogMs = Math.max(15000, entry.text.length * 120); // ~120ms per char or 15s min
    const watchdog = setTimeout(() => {
      if (this.speaking) {
        console.warn('[TTS] Watchdog: speech timed out after', watchdogMs, 'ms — forcing advance');
        window.speechSynthesis.cancel();
        this.speaking = false;
        entry.resolve();
        this.processQueue();
      }
    }, watchdogMs);

    utterance.onend = () => {
      clearTimeout(watchdog);
      this.speaking = false;
      entry.resolve();
      this.processQueue();
    };

    utterance.onerror = (event: SpeechSynthesisErrorEvent) => {
      clearTimeout(watchdog);
      this.speaking = false;
      if (event.error === 'canceled') {
        entry.resolve();
      } else {
        console.warn('[TTS] Speech error:', event.error);
        entry.reject(new Error(`Speech synthesis error: ${event.error}`));
      }
      this.processQueue();
    };

    window.speechSynthesis.speak(utterance);

    // Mobile Chrome bug: speechSynthesis can pause itself on long utterances.
    // Periodically resume it during playback.
    const resumeInterval = setInterval(() => {
      if (!this.speaking) {
        clearInterval(resumeInterval);
        return;
      }
      if (window.speechSynthesis.paused) {
        console.log('[TTS] Resuming paused speech');
        window.speechSynthesis.resume();
      }
    }, 5000);

    utterance.onend = () => {
      clearTimeout(watchdog);
      clearInterval(resumeInterval);
      this.speaking = false;
      entry.resolve();
      this.processQueue();
    };

    utterance.onerror = (event: SpeechSynthesisErrorEvent) => {
      clearTimeout(watchdog);
      clearInterval(resumeInterval);
      this.speaking = false;
      if (event.error === 'canceled') {
        entry.resolve();
      } else {
        console.warn('[TTS] Speech error:', event.error);
        entry.reject(new Error(`Speech synthesis error: ${event.error}`));
      }
      this.processQueue();
    };
  }
}

const ttsEngine = new TTSEngine();

// -----------------------------------------------------------------------
// Mobile TTS unlock — iOS Safari requires user gesture
// -----------------------------------------------------------------------

let ttsUnlocked = false;
let pendingText: string | null = null;
let pendingResolve: (() => void) | null = null;

function onFirstInteraction(): void {
  if (ttsUnlocked) return;
  ttsUnlocked = true;
  console.log('[TTS] Unlocked by user gesture');

  // Remove listeners
  document.removeEventListener('touchstart', onFirstInteraction, true);
  document.removeEventListener('touchend', onFirstInteraction, true);
  document.removeEventListener('click', onFirstInteraction, true);

  // If there's a queued greeting, speak it now
  if (pendingText) {
    const text = pendingText;
    const resolve = pendingResolve;
    pendingText = null;
    pendingResolve = null;

    // On iOS, we must speak synchronously within the gesture callback
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = DEFAULT_RATE;
    utterance.pitch = DEFAULT_PITCH;
    utterance.volume = DEFAULT_VOLUME;
    // Use current language voice and tag
    const langMeta = getLangMeta();
    utterance.lang = langMeta.speechLang;
    const langVoice = getVoiceForCurrentLang();
    if (langVoice) utterance.voice = langVoice;
    else if (selectedVoice) utterance.voice = selectedVoice;
    utterance.onend = () => { if (resolve) resolve(); };
    utterance.onerror = () => { if (resolve) resolve(); };
    window.speechSynthesis.speak(utterance);
  }
}

if (typeof document !== 'undefined') {
  document.addEventListener('touchstart', onFirstInteraction, true);
  document.addEventListener('touchend', onFirstInteraction, true);
  document.addEventListener('click', onFirstInteraction, true);
}

/**
 * Try to speak. If blocked on mobile (no user gesture yet), queue it
 * and it will play when the user first taps the screen.
 *
 * Workaround for mobile browsers: speechSynthesis can go into a "paused"
 * state or silently fail. We detect this and force-resume.
 */
function speakWithFallback(text: string, options?: TTSOptions): Promise<void> {
  if (isMuted()) return Promise.resolve();

  // Try Kokoro neural TTS for English — dramatically better quality
  const langMeta = getLangMeta();
  const isEnglish = langMeta.speechLang.startsWith('en');

  if (isEnglish && isKokoroReady()) {
    return kokoroSpeak(text, { speed: options?.rate ?? DEFAULT_RATE }).catch((err) => {
      console.warn('[TTS] Kokoro failed, falling back to Web Speech API:', err);
      return webSpeechFallback(text, options);
    });
  }

  return webSpeechFallback(text, options);
}

/** Web Speech API fallback — works for all languages */
function webSpeechFallback(text: string, options?: TTSOptions): Promise<void> {
  // Force-resume speechSynthesis if it's stuck in paused state (common mobile bug)
  if (window.speechSynthesis.paused) {
    window.speechSynthesis.resume();
  }

  // Cancel any stale/stuck utterances before speaking new content
  if (!window.speechSynthesis.speaking && !window.speechSynthesis.pending) {
    window.speechSynthesis.cancel();
  }

  // If already unlocked or not on a gesture-restricted platform, speak directly
  if (ttsUnlocked) {
    return ttsEngine.speak(text, options);
  }

  // Try speaking directly — this works on desktop and Android
  return new Promise<void>((resolve) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = options?.rate ?? DEFAULT_RATE;
    utterance.pitch = options?.pitch ?? DEFAULT_PITCH;
    utterance.volume = options?.volume ?? DEFAULT_VOLUME;
    // Use current language voice and tag
    const langMeta = getLangMeta();
    utterance.lang = langMeta.speechLang;
    const langVoice = getVoiceForCurrentLang();
    if (langVoice) utterance.voice = langVoice;
    else if (selectedVoice) utterance.voice = selectedVoice;

    let spoke = false;

    utterance.onstart = () => {
      spoke = true;
      ttsUnlocked = true;
      // Remove gesture listeners since TTS works
      document.removeEventListener('touchstart', onFirstInteraction, true);
      document.removeEventListener('touchend', onFirstInteraction, true);
      document.removeEventListener('click', onFirstInteraction, true);
    };

    utterance.onend = () => {
      resolve();
    };

    utterance.onerror = (event: SpeechSynthesisErrorEvent) => {
      if (!spoke && event.error !== 'canceled') {
        // Speech was blocked — queue for user gesture
        console.log('[TTS] Speech blocked, queuing for user gesture:', event.error);
        pendingText = text;
        pendingResolve = resolve;
      } else {
        resolve();
      }
    };

    window.speechSynthesis.speak(utterance);

    // Safety: if nothing happens after 2s, queue for gesture
    setTimeout(() => {
      if (!spoke && !ttsUnlocked) {
        console.log('[TTS] Speech did not start after 2s, queuing for user gesture');
        pendingText = text;
        pendingResolve = resolve;
      }
    }, 2000);
  });
}

export const speak = speakWithFallback;
export function stop(): void {
  kokoroStop(); // Stop neural TTS if active
  ttsEngine.stop();
  // Also stop any HTML5 Audio currently playing
  if (currentPlayingAudio) {
    currentPlayingAudio.pause();
    currentPlayingAudio.currentTime = 0;
    currentPlayingAudio = null;
  }
  if (pendingAudio) {
    pendingAudio.pause();
    pendingAudio = null;
    if (pendingAudioResolve) { pendingAudioResolve(); pendingAudioResolve = null; }
  }
}
export function isSpeaking(): boolean {
  return ttsEngine.isSpeaking() || isKokoroSpeaking();
}
export const setRate = ttsEngine.setRate.bind(ttsEngine);
export const setVolume = ttsEngine.setVolume.bind(ttsEngine);
export const setPitch = ttsEngine.setPitch.bind(ttsEngine);

// -----------------------------------------------------------------------
// Pre-recorded audio playback — bypasses Web Speech API entirely
// -----------------------------------------------------------------------

/**
 * Play a pre-recorded audio file (MP3/M4A) via HTML5 Audio.
 *
 * Why this exists:
 * iOS Safari blocks Web Speech API until after a user gesture, making
 * auto-play greetings impossible. HTML5 Audio has the same restriction,
 * BUT it can be played from within a user-gesture callback (e.g., the
 * first touchend/click that opens the app). We attach a one-shot listener
 * that plays the audio file on first user interaction, which is a far more
 * reliable pattern than the Web Speech API fallback queue.
 *
 * Returns a Promise that resolves when playback ends (or rejects on error).
 */
// Track current playing audio so stop() can halt it
let currentPlayingAudio: HTMLAudioElement | null = null;

export function playAudioFile(url: string): Promise<void> {
  if (isMuted()) return Promise.resolve();

  return new Promise<void>((resolve, reject) => {
    const audio = new Audio(url);
    audio.preload = 'auto';
    currentPlayingAudio = audio;

    audio.onended = () => { currentPlayingAudio = null; resolve(); };
    audio.onerror = () => { currentPlayingAudio = null; reject(new Error(`Audio playback failed: ${url}`)); };

    const playAttempt = audio.play();
    if (playAttempt !== undefined) {
      playAttempt.catch((err) => {
        // Autoplay blocked — queue for first user gesture
        console.log('[TTS] Audio autoplay blocked, queuing for gesture:', err.name);
        pendingAudio = audio;
        pendingAudioResolve = resolve;
      });
    }
  });
}

// Track a pending audio element waiting for user gesture
let pendingAudio: HTMLAudioElement | null = null;
let pendingAudioResolve: (() => void) | null = null;

/** Returns true if a greeting audio is queued, waiting for a user gesture */
export function hasAudioPending(): boolean {
  return pendingAudio !== null;
}

function onFirstInteractionForAudio(): void {
  if (!pendingAudio) return;
  const audio = pendingAudio;
  const resolve = pendingAudioResolve;
  pendingAudio = null;
  pendingAudioResolve = null;

  audio.play().then(() => {
    audio.onended = () => { if (resolve) resolve(); };
  }).catch(() => {
    // Still blocked — give up gracefully, let TTS fallback handle it
    if (resolve) resolve();
  });

  document.removeEventListener('touchstart', onFirstInteractionForAudio, true);
  document.removeEventListener('touchend', onFirstInteractionForAudio, true);
  document.removeEventListener('click', onFirstInteractionForAudio, true);
}

if (typeof document !== 'undefined') {
  document.addEventListener('touchstart', onFirstInteractionForAudio, true);
  document.addEventListener('touchend', onFirstInteractionForAudio, true);
  document.addEventListener('click', onFirstInteractionForAudio, true);
}

/**
 * Greeting audio map — time-of-day MP3 file selection.
 * Returns the correct pre-recorded file path based on current hour.
 */
export function getGreetingAudioUrl(patientName?: string | null): string {
  if (patientName) return '/audio/greeting-personalized.mp3';
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return '/audio/greeting-morning.mp3';
  if (hour >= 12 && hour < 17) return '/audio/greeting-afternoon.mp3';
  if (hour >= 17 && hour < 21) return '/audio/greeting-evening.mp3';
  // Night (before 5am or after 9pm) — use morning as closest neutral
  return '/audio/greeting-morning.mp3';
}

/**
 * Play the time-appropriate greeting MP3, falling back to Web Speech API
 * if audio file playback fails (e.g., file missing during development).
 *
 * This is the function main.ts should call on startup.
 */
export async function speakGreeting(fallbackText: string, patientName?: string | null): Promise<void> {
  if (isMuted()) return;

  const audioUrl = getGreetingAudioUrl(patientName);

  try {
    console.log('[TTS] Playing pre-recorded greeting:', audioUrl);
    await playAudioFile(audioUrl);
    console.log('[TTS] Pre-recorded greeting complete');
  } catch (err) {
    console.warn('[TTS] Pre-recorded greeting failed, falling back to Web Speech API:', err);
    try {
      await speakWithFallback(fallbackText);
    } catch (ttsErr) {
      console.warn('[TTS] Web Speech API fallback also failed:', ttsErr);
    }
  }
}
